import { useEffect, useState } from "react";
import type { TransferPlace } from "@majourneys/shared";

const STORAGE_KEY = "majourneys.bookingDraft";
const EVENT_NAME = "majourneys:booking-draft";
/** Drafts older than this are ignored on read so stale state doesn't stick. */
const MAX_AGE_MS = 24 * 60 * 60 * 1000;
/**
 * Contact details (name, email, phone, address, special requests) are kept
 * until the end of the day they were typed, Moroccan time, then stripped
 * (owner, 2026-10-12; it was one hour, which wiped them from a guest who
 * stepped away and came back the same evening).
 */
const moroccoDay = (ms: number): string =>
  new Intl.DateTimeFormat("en-CA", { timeZone: "Africa/Casablanca" }).format(new Date(ms));

export function contactDetailsExpired(savedAt: number, now: number): boolean {
  return moroccoDay(savedAt) !== moroccoDay(now);
}

export interface BookingDraft {
  step: number;
  pkgId: string;
  date: string;
  /** ISO check-out date for stay-flow bookings (typePrice === 2). */
  checkOut: string;
  time: string;
  hours: number;
  groups: number;
  /** Stays only: tents or lodges booked. Optional: older drafts have none. */
  rooms?: number;
  adults: number;
  children: number;
  infants: number;
  transport: boolean;
  transportOutside: boolean;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  transportAddress: string;
  /**
   * Special requests. Optional because drafts written before the details step
   * grew the field are still in visitors' localStorage.
   */
  notes?: string;
  /**
   * Spa only: how many of each treatment, by package id. Optional, like the
   * two below, so drafts saved before they existed still read.
   */
  packQty?: Record<string, number>;
  /** Spa only: woman or man per guest, null where not answered yet (see guest-mix.tsx). */
  guestMix?: ("woman" | "man" | null)[];
  /** Nightclub sheet only: guests at the table (`adults` is the ticket count). */
  partySize?: number;
  /**
   * The phone's dial code alone ("+212"). Kept when the number itself is
   * cleared after an hour, so the guest finds their own country again rather
   * than the one the browser guesses.
   */
  phoneDial?: string;
  /** The pickup picked from the place search (majourneys.com), not just its text. */
  pickupPlace?: TransferPlace | null;
  /** "Not sure yet": the pickup address follows on WhatsApp. */
  addressLater?: boolean;
  /** Restaurants: the "Ride there and back" switch. */
  rideOn?: boolean;
  /** Restaurants: where the ride collects the guest. */
  ridePickup?: TransferPlace | null;
}

/** The dial code at the front of a stored phone ("+212 612..." gives "+212"). */
export function phoneDialOf(phone: string | undefined): string {
  return /^(\+\d{1,4})(?:\s|$)/.exec((phone ?? "").trim())?.[1] ?? "";
}

/**
 * The draft with what identifies the guest taken out: contact, addresses and
 * places, special requests. What they chose (package, day, time, party, the
 * ride switch) is kept, and so is the phone's country.
 */
function withoutPii<T extends BookingDraft>(draft: T): T {
  return {
    ...draft,
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    phoneDial: draft.phoneDial || phoneDialOf(draft.phone),
    transportAddress: "",
    // Special requests carry dietary and accessibility details: the most
    // sensitive thing on the form, so it goes with the rest of the PII.
    notes: "",
    pickupPlace: null,
    ridePickup: null,
  };
}

/**
 * The details page and the nightclub sheet keep their drafts in the same store
 * under a suffixed key (`<slug>#details`, `<slug>#night`) so they share the
 * 24h expiry and the 1h PII strip without overwriting the booking sheet's.
 * They are not resumable sheet drafts, so the "your drafts" list skips them.
 */
export function isSuffixedDraftKey(key: string): boolean {
  return key.includes("#");
}

/** Draft key for the details page (packages, details, checkout flow). */
export function detailsDraftKey(activitySlug: string): string {
  return activitySlug ? `${activitySlug}#details` : "";
}

/** Draft key for the nightclub sheet. */
export function nightDraftKey(activitySlug: string): string {
  return activitySlug ? `${activitySlug}#night` : "";
}

interface StoredDraft extends BookingDraft {
  savedAt: number;
}

interface DraftMap {
  [activitySlug: string]: StoredDraft;
}

function readMap(): DraftMap {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return {};
    return parsed as DraftMap;
  } catch {
    return {};
  }
}

function writeMap(map: DraftMap) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    /* quota / disabled storage — silent */
  }
}

function notifyChange(activitySlug: string, draft: BookingDraft | null): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent(EVENT_NAME, { detail: { activitySlug, draft } }),
  );
}

export function saveBookingDraft(activitySlug: string, draft: BookingDraft): void {
  if (!activitySlug) return;
  const map = readMap();
  map[activitySlug] = { ...draft, savedAt: Date.now() };
  writeMap(map);
  notifyChange(activitySlug, draft);
}

export function getBookingDraft(activitySlug: string): BookingDraft | null {
  if (!activitySlug) return null;
  const map = readMap();
  const entry = map[activitySlug];
  if (!entry) return null;
  const now = Date.now();
  const age = now - entry.savedAt;
  if (age > MAX_AGE_MS) {
    clearBookingDraft(activitySlug);
    return null;
  }
  const { savedAt: _omit, ...rest } = entry;
  // Strip PII (name/email/phone/address) after the day ends while keeping the
  // package/date/guests selection that's safe to persist longer.
  if (contactDetailsExpired(entry.savedAt, now)) return withoutPii(rest);
  return rest;
}

export function clearBookingDraft(activitySlug: string): void {
  if (!activitySlug) return;
  const map = readMap();
  if (!map[activitySlug]) return;
  delete map[activitySlug];
  writeMap(map);
  notifyChange(activitySlug, null);
}

export interface NamedBookingDraft extends BookingDraft {
  /** Activity slug this draft belongs to. */
  slug: string;
  /** Last save timestamp (ms). */
  savedAt: number;
}

/**
 * Returns every active draft (passes the 24h TTL) keyed under any activity
 * slug, sorted newest first. PII is stripped from drafts older than 1h
 * (same rule as getBookingDraft).
 */
export function getAllBookingDrafts(): NamedBookingDraft[] {
  const map = readMap();
  const now = Date.now();
  const out: NamedBookingDraft[] = [];
  for (const [slug, entry] of Object.entries(map)) {
    if (isSuffixedDraftKey(slug)) continue;
    const age = now - entry.savedAt;
    if (age > MAX_AGE_MS) continue;
    if (contactDetailsExpired(entry.savedAt, now)) {
      out.push(withoutPii({ ...entry, slug }));
    } else {
      out.push({ ...entry, slug });
    }
  }
  return out.sort((a, b) => b.savedAt - a.savedAt);
}

/** Hook variant that subscribes to changes in any draft. */
export function useAllBookingDrafts(): NamedBookingDraft[] {
  const [drafts, setDrafts] = useState<NamedBookingDraft[]>(() => getAllBookingDrafts());
  useEffect(() => {
    const sync = () => setDrafts(getAllBookingDrafts());
    sync();
    function onSameTab() {
      sync();
    }
    function onCrossTab(e: StorageEvent) {
      if (e.key === STORAGE_KEY) sync();
    }
    window.addEventListener(EVENT_NAME, onSameTab);
    window.addEventListener("storage", onCrossTab);
    return () => {
      window.removeEventListener(EVENT_NAME, onSameTab);
      window.removeEventListener("storage", onCrossTab);
    };
  }, []);
  return drafts;
}

/**
 * React hook: returns the current booking draft for `activitySlug`, kept in
 * sync with same-tab updates (custom event) and cross-tab updates (storage).
 */
export function useBookingDraft(activitySlug: string | undefined): BookingDraft | null {
  const [draft, setDraft] = useState<BookingDraft | null>(() =>
    activitySlug ? getBookingDraft(activitySlug) : null,
  );

  useEffect(() => {
    if (!activitySlug) {
      setDraft(null);
      return;
    }
    const slug = activitySlug;
    setDraft(getBookingDraft(slug));

    function handleSameTab(e: Event) {
      const custom = e as CustomEvent<{ activitySlug: string; draft: BookingDraft | null }>;
      if (custom.detail?.activitySlug !== slug) return;
      setDraft(custom.detail.draft);
    }
    function handleCrossTab(e: StorageEvent) {
      if (e.key !== STORAGE_KEY) return;
      setDraft(getBookingDraft(slug));
    }

    window.addEventListener(EVENT_NAME, handleSameTab);
    window.addEventListener("storage", handleCrossTab);
    return () => {
      window.removeEventListener(EVENT_NAME, handleSameTab);
      window.removeEventListener("storage", handleCrossTab);
    };
  }, [activitySlug]);

  return draft;
}

/** True if a draft exists AND has at least one user-entered value (i.e. the
 * user actually started the flow — not just an automatic preflight). */
export function hasMeaningfulDraft(draft: BookingDraft | null): boolean {
  if (!draft) return false;
  return (
    draft.step > 0 ||
    draft.firstName.trim().length > 0 ||
    draft.lastName.trim().length > 0 ||
    draft.email.trim().length > 0 ||
    draft.phone.replace(/\D/g, "").length > 0 ||
    draft.transport
  );
}
